import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import React, { useEffect, useState } from 'react';

import { Button, Icon, Spinner, Text } from '@poliedro/tamentai';

import { useMutation, useQuery } from '@tanstack/react-query';

import { logLoggin } from '../../../services/Log/index';
import { getTeacherInfo } from '../../../services/School/index';
import { setClassesFormatadas } from '../../../store/classesSlice';
import { resetProfile, setProfileData } from '../../../store/profileSlice';
import { SelectStyles } from '../../../styles/GlobalStyles';
import { ProfileData } from '../../../types/Profile';
import { formatClasses } from '../../../utils/formatClasses';
import { ProfileSelectorModalProps } from '../types';
import { ButtonWrapper, Modal, SelectWrapper, StyledSelect } from './style';

const PMAIS_URL = import.meta.env.VITE_PMAIS_URL;

const ProfileSelectorModal: React.FC<ProfileSelectorModalProps> = ({
  isOpen,
  userData,
}) => {
  const [filteredSchool, setFilteredSchool] = useState<string>('');
  const [filteredProfile, setFilteredProfile] = useState<string>('');
  const [hasLogged, setHasLogged] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) {
      setHasLogged(false);
    }
  }, [isOpen]);

  if (!isOpen || !userData) return null;

  useEffect(() => {
    if (isOpen && !filteredSchool) {
      const lastSchoolId = sessionStorage.getItem('lastSchoolId');
      if (lastSchoolId) {
        setFilteredSchool(lastSchoolId);
      }
    }
  }, [isOpen, filteredSchool]);

  const {
    data: TeacherInfo,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['TeacherInfo', filteredProfile],
    queryFn: () => getTeacherInfo(Number(filteredSchool) as number),
    enabled: false,
  });

  const { mutateAsync: logLoginMutate } = useMutation({
    mutationKey: ['log', 'login'],
    mutationFn: (args: {
      UserId: number;
      UserName: string;
      SchoolId: string;
      SchoolName: string;
      ProfileId: string;
      ProfileName: string;
    }) =>
      logLoggin(
        args.UserId,
        args.UserName,
        args.SchoolId,
        args.SchoolName,
        args.ProfileId,
        args.ProfileName,
      ),
    retry: 1,
    networkMode: 'always',
  });

  const escolaOptions = userData.schools
    .map(escola => ({
      label: [
        escola.id_school,
        escola.name,
        escola.neighborhood,
        escola.city && escola.state ? `${escola.city}/${escola.state}` : escola.city,
        escola.segments_active?.join(' / '),
      ]
        .filter(Boolean)
        .join(' – '),
      value: String(escola.id_school),
      profile:
        escola.profile
          .map(perfil => ({
            label: perfil.name,
            value: String(perfil.id),
          }))
          .filter(item =>
            ['Professor', 'Coordenador', 'Escola', 'Aluno'].includes(item.label),
          ) || [],
    }))
    .filter(item => item.profile.length > 0);

  const perfilOptions =
    escolaOptions.find(escola => escola.value === filteredSchool)?.profile || [];

  const selectedSchool = userData.schools.find(
    escola => String(escola.id_school) === filteredSchool,
  );

  const selectedPerfil = selectedSchool?.profile.find(
    profile => String(profile.id) === filteredProfile,
  );

  const selectedPerfilNome = selectedPerfil?.name.toLowerCase() ?? '';

  const isButtonDisabled =
    !filteredSchool ||
    filteredSchool.trim() === '' ||
    !filteredProfile ||
    filteredProfile.trim() === '' ||
    !['professor', 'coordenador', 'escola', 'aluno'].includes(selectedPerfilNome);

  const isProfessor = selectedPerfil?.name.toLocaleLowerCase() === 'professor';

  const handleAccessClick = async () => {
    if (
      isButtonDisabled ||
      !userData ||
      !selectedSchool ||
      !selectedPerfil ||
      hasLogged
    )
      return;

    setHasLogged(true);

    let formatted: Record<string, string[]> = {};
    let teacherClasses: { segmentId: number; classId: number }[] = [];

    let teacherInfoData = TeacherInfo;

    if (isProfessor) {
      const { data } = await refetch();
      teacherInfoData = data;
      if (!teacherInfoData) return;
    }

    if (isProfessor && teacherInfoData) {
      formatted = formatClasses(teacherInfoData);
      dispatch(setClassesFormatadas(formatted));

      teacherClasses = teacherInfoData.map(info => ({
        segmentId: info.segmentId,
        classId: info.classId,
      }));
    }

    const enderecoParts = [
      selectedSchool.neighborhood,
      selectedSchool.city
        ? selectedSchool.state
          ? `${selectedSchool.city}/${selectedSchool.state}`
          : selectedSchool.city
        : null,
    ].filter(Boolean);

    const enderecoEscola = enderecoParts.join(' - ');

    const profileData: ProfileData = {
      id: userData.id,
      idEscola: selectedSchool.id_school ?? null,
      nomeEscola: selectedSchool.name ?? null,
      enderecoEscola: enderecoEscola,
      segmentos: selectedSchool.segments_active ?? null,
      nome: userData.name,
      emailp4ed: userData.email_p4ed,
      foto: userData.photo,
      perfil: {
        id: selectedPerfil.id ?? null,
        nome: selectedPerfil.name ?? '',
      },
      classes: formatted,
      skipSurvey: false,
      isImpersonated: userData.isImpersonated,
      TeacherSegments: [
        ...new Set(
          teacherInfoData?.map(information => {
            return information.segmentName;
          }),
        ),
      ],
      TeacherClasses: teacherClasses,
      sapIds: userData.sap_ids ?? null,
      userClasses: userData.classes ?? null,
    };

    try {
      logLoginMutate({
        UserId: profileData.id,
        UserName: profileData.nome,
        SchoolId: String(profileData.idEscola ?? ''),
        SchoolName: profileData.nomeEscola ?? '',
        ProfileId: String(profileData.perfil.id ?? ''),
        ProfileName: profileData.perfil.nome,
      });
    } catch (e) {
      console.error(e);
    }

    dispatch(setProfileData(profileData));

    if (
      import.meta.env.VITE_ENVIRONMENT === 'production' &&
      (window as Window & { clarity?: Function }).clarity
    ) {
      (window as Window & { clarity?: Function }).clarity?.(
        'set',
        'tipo_usuario',
        profileData.perfil.nome,
      );
    }

    sessionStorage.removeItem('lastSchoolId');
    navigate('/');
  };

  const verifyOneOption =
    escolaOptions.length === 1 && escolaOptions[0].profile.length === 1;
  const checkDifferent = !filteredSchool || !filteredProfile;
  const filteredOptions =
    filteredSchool === escolaOptions[0].value &&
    filteredProfile === escolaOptions[0].profile[0].value;

  useEffect(() => {
    if (isOpen && verifyOneOption) {
      if (checkDifferent) {
        setFilteredSchool(escolaOptions[0].value);
        setFilteredProfile(escolaOptions[0].profile[0].value);
      } else if (filteredOptions) {
        handleAccessClick();
      }
    }
  }, [isOpen, escolaOptions, filteredSchool, filteredProfile]);

  const isAutoAccess =
    isOpen && verifyOneOption && (checkDifferent || filteredOptions);

  if (isAutoAccess) return null;

  const handleCancel = () => {
    localStorage.removeItem('token');
    dispatch(resetProfile());
    window.location.href = PMAIS_URL!;
  };

  return (
    <Modal>
      <Text color="#1a1a1a" variant="display" style={{ fontSize: '28px' }}>
        Selecione unidade e perfil
      </Text>
      <SelectWrapper>
        <SelectStyles />
        <StyledSelect
          searchable
          label="Unidade"
          onChangeValue={value => {
            setFilteredSchool(String(value));
            setFilteredProfile('');
          }}
          options={escolaOptions}
          size="lg"
          required
          value={filteredSchool}
          placeholder="Nome da Unidade"
          isPlaceholder={!filteredSchool}
        />
        <StyledSelect
          searchable
          label="Perfil"
          onChangeValue={value => setFilteredProfile(String(value))}
          options={perfilOptions}
          size="lg"
          required
          value={filteredProfile}
          placeholder="Perfil"
          isPlaceholder={!filteredProfile}
        />
      </SelectWrapper>
      <ButtonWrapper>
        <Button size="md" variant="neutral" onClick={handleCancel}>
          Cancelar
        </Button>
        <Button
          size="md"
          variant="primary"
          onClick={handleAccessClick}
          disabled={isButtonDisabled || isLoading}
          style={{
            color: isLoading ? '#000000' : '#FAFAFA',
          }}
        >
          {isLoading && <Spinner borderWidth={3} size={18} />}
          {isLoading ? 'Aguarde...' : 'Acessar'}

          <Icon
            color={isButtonDisabled ? '#ccc' : '#FFFFFF'}
            name="ArrowRight"
            size="md"
          />
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export default ProfileSelectorModal;
